import React, { useState, useEffect } from 'react';
import { Clock, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { formatDuration } from '../../../lib/constants';

const PRESETS = [15, 30, 45, 60, 90, 120, 180, 240];
// BG_34: cap the custom duration at 24 hours so the field can no longer
// accept unbounded / "infinite" minute values.
const MAX_CUSTOM_MINUTES = 24 * 60;

const DurationPicker = ({ value = 60, onChange, children, disabled = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // BG_37: keep the raw input string while editing so the field can be
  // emptied; parsing/validation only happens when the value is committed.
  const [customVal, setCustomVal] = useState(String(value));

  // Re-sync the editable field with the current duration whenever it opens.
  useEffect(() => {
    if (open) setCustomVal(String(value));
  }, [open, value]);

  const customEmpty = customVal.trim() === '';
  const parsedCustom = parseInt(customVal, 10);
  const customOutOfRange =
    !customEmpty && (Number.isNaN(parsedCustom) || parsedCustom < 1 || parsedCustom > MAX_CUSTOM_MINUTES);

  const commitCustom = () => {
    // BG_34: never apply an empty or out-of-range value.
    if (customEmpty || customOutOfRange) return;
    onChange(parsedCustom);
    setOpen(false);
  };

  const defaultTrigger = (
    <button disabled={disabled} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' :
      value !== 60 ? 'text-purple-400 bg-purple-500/10' : 'text-muted-foreground hover:bg-accent/60'
    }`}>
      <Clock className="w-3.5 h-3.5" />
      <span>{formatDuration(value, t)}</span>
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3 bg-accent border-border max-md:!w-screen max-md:!max-w-none max-md:!rounded-b-none" align="start" sideOffset={4}>
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {PRESETS.map(mins => (
            <button
              key={mins}
              onClick={() => { onChange(mins); setOpen(false); }}
              className={`px-1.5 py-1.5 rounded-md text-xs transition-colors ${
                value === mins ? 'bg-purple-500 text-white' : 'bg-accent/60 text-foreground/80 hover:bg-accent'
              }`}
            >
              {formatDuration(mins, t)}
            </button>
          ))}
        </div>
        <div className="border-t border-border pt-2.5">
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={customVal}
              onChange={(e) => setCustomVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitCustom();
              }}
              className={`flex-1 px-2.5 py-1.5 bg-accent/60 border rounded-md text-xs text-foreground w-full ${
                customOutOfRange ? 'border-red-500' : 'border-border'
              }`}
              min={1}
              max={MAX_CUSTOM_MINUTES}
              step={5}
            />
            <span className="text-muted-foreground text-xs">{t('common.min')}</span>
            <button
              onClick={commitCustom}
              disabled={customEmpty || customOutOfRange}
              className="p-1.5 bg-accent/60 hover:bg-accent rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check className="w-3.5 h-3.5 text-foreground/80" />
            </button>
          </div>
          {customOutOfRange && (
            <p className="text-red-400 text-[11px] mt-1.5">
              {t('ui_task.duration.range_hint', { max: MAX_CUSTOM_MINUTES })}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DurationPicker;
